import { FC, useEffect, useMemo, useState } from 'react';

import { CircularProgress, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Loader } from '../../../../../../components';
import { RewardRuleEffects } from '../../../../../../types';
import { convertFromNairaToKobo, formatCart } from '../../../../../../utils/helper';
import { CHECKOUT_SESSION_KEY, usePayLinkCart } from '../../../../context/PayLinkCartContext';
import { TPayLinkReward, usePayLinkUser } from '../../../../context/PaylinkUserContext';
import { getAdminDetails, getEarnableRewards } from '../../../../services/api';
import { TShiping } from '../../../../types/walllet';

const RadioCardBox = styled("div")(({ theme }) => ({
  "& .selected": {
    border: "1px solid #FF5349",
    boxShadow: "2px 2px 8px 0px rgba(237, 33, 21, 0.20)",
  },

  "& .sectionTitle": {
    fontSize: "1.25rem",
    fontWeight: 500,
    color: "#0F0F10",
  },

  "& .sectionNote": {
    fontSize: "0.75em",
    fontWeight: 500,
    color: "#00AE97",
    marginTop: "0.75rem",
    display: "block",
  },

  "& .helperText": {
    fontSize: "14px",
    color: "#4C4D52",
    marginTop: "14px",
  },

  "& .card": {
    padding: "1rem",
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "row",
    cursor: "pointer",
    transition: "0.3s all ease-in-out",
  },

  "& .title": {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#0F0F10",
  },

  "& .balance": {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#696A71",
  },

  "& .radioOuter": {
    width: "24px",
    height: "24px",
    borderRadius: "999px",
    border: "2px solid #C4C4C4",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "0.75rem",
    marginTop: "0.1rem",
    flexShrink: 0,
  },

  "& .radioOuter.selected": {
    borderColor: "#FF5349",
    boxShadow: "none",
  },

  "& .radioInner": {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    background: "#FF5349",
  },
}));

interface IProps {
  isEnrolmentLoading?: boolean;
}

export const RewardCard: FC<IProps> = ({ isEnrolmentLoading = false }) => {
  const {
    reward: selectedReward,
    freeShippingReward: selectedShippingReward,
    setReward,
    setFreeShippingReward,
    adminDetails,
    user,
    shippingRate,
  } = usePayLinkUser();
  const { storeProfile, sessionUniqueReferenceNumber, cartItems } =
    usePayLinkCart();

  const { data: localAdminData } = useQuery({
    queryKey: ["PayLinkRewardAdminDetails"],
    queryFn: () => getAdminDetails({ accountType: "Admin" }),
    retry: 0,
    staleTime: Infinity,
  });

  const resolvedAdminUrn =
    adminDetails?.uniqueReferenceNumber ||
    localAdminData?.data?.[0]?.uniqueReferenceNumber ||
    "";

  const effectiveSessionUrn =
    sessionUniqueReferenceNumber ||
    localStorage.getItem(CHECKOUT_SESSION_KEY) ||
    "";

  const canFetchRewards =
    !!user?.consumerUniqueReferenceNumber &&
    !!user?.walletUniqueReferenceNumber &&
    !!storeProfile?.merchantUniqueReferenceNumber &&
    !!storeProfile?.customerUniqueReferenceNumber &&
    !!effectiveSessionUrn;

  const formattedCart = useMemo(
    () =>
      formatCart(
        (cartItems || []).map((item: any) => ({
          ...item,
          image:
            item?.image || item?.productImages?.[0]?.documentAbsoluteUri || "",
          price: convertFromNairaToKobo(Number(item?.price || 0)),
        })),
      ),
    [cartItems],
  );

  const { data: rewardsData, isLoading: isRewardsLoading } = useQuery({
    queryKey: ["PayLinkApplicableRewards", effectiveSessionUrn],
    queryFn: () =>
      getEarnableRewards({
        adminUniqueReferenceNumber:
          resolvedAdminUrn || storeProfile?.merchantUniqueReferenceNumber || "",
        consumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
        walletUniqueReferenceNumber:
          user?.walletUniqueReferenceNumber as string,
        merchantUniqueReferenceNumber:
          storeProfile?.merchantUniqueReferenceNumber as string,
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber as string,
        uniqueReferenceNumber: effectiveSessionUrn,
        cart: formattedCart,
        shipping: shippingRate as TShiping,
      }),
    enabled: canFetchRewards,
    retry: 0,
    staleTime: Infinity,
  });

  const applicableRewards = useMemo(() => {
    const normalizeReward = (reward: any) => {
      const loyaltyPromotion = reward?.loyaltyPromotion || {};

      return {
        ...reward,
        uniqueReferenceNumber:
          reward?.uniqueReferenceNumber ||
          reward?.rewardUniqueReferenceNumber ||
          reward?.promotionUniqueReferenceNumber ||
          loyaltyPromotion?.promotionUniqueReferenceNumber ||
          "",
        promotionUniqueReferenceNumber:
          reward?.promotionUniqueReferenceNumber ||
          reward?.rewardUniqueReferenceNumber ||
          loyaltyPromotion?.promotionUniqueReferenceNumber ||
          "",
        customerUniqueReferenceNumber:
          reward?.customerUniqueReferenceNumber ||
          loyaltyPromotion?.customerUniqueReferenceNumber ||
          "",
        loyaltyProgrammeTierUniqueReferenceNumber:
          reward?.loyaltyProgrammeTierUniqueReferenceNumber ||
          loyaltyPromotion?.loyaltyProgrammeTierUniqueReferenceNumber ||
          "",
        rewardRules:
          reward?.rewardRules ||
          loyaltyPromotion?.rewardRules ||
          reward?.rules ||
          [],
        rewardEffect:
          reward?.rewardEffect || loyaltyPromotion?.rewardEffect || "",
        rewardEffectValue:
          reward?.rewardEffectValue ?? loyaltyPromotion?.rewardEffectValue ?? 0,
        rewardEffectValueLimit:
          reward?.rewardEffectValueLimit ??
          loyaltyPromotion?.rewardEffectValueLimit ??
          0,
        rewardName:
          reward?.rewardName || loyaltyPromotion?.promotionName || "Reward",
      };
    };

    const sessionApplicable = Array.isArray(
      rewardsData?.data?.sessionRewardsApplicable,
    )
      ? rewardsData?.data?.sessionRewardsApplicable
      : [];

    const normalizedSessionRewards = sessionApplicable.map(normalizeReward);
    return normalizedSessionRewards.filter(
      (reward: any) =>
        !!reward?.uniqueReferenceNumber || !!reward?.promotionUniqueReferenceNumber,
    );
  }, [rewardsData]);

  const rewardsToDisplay = applicableRewards;
  const shippingRewards = useMemo(
    () =>
      rewardsToDisplay.filter(
        (reward: TPayLinkReward | null) =>
          reward?.rewardEffect === RewardRuleEffects.FreeShipping,
      ),
    [rewardsToDisplay],
  );
  const regularRewards = useMemo(
    () =>
      rewardsToDisplay.filter(
        (reward: TPayLinkReward | null) =>
          reward?.rewardEffect !== RewardRuleEffects.FreeShipping,
      ),
    [rewardsToDisplay],
  );
  const hasShippingRewards = shippingRewards.length > 0;

  const isLoading = isRewardsLoading;
  const shouldShowEnrolmentLoader =
    isEnrolmentLoading && (isLoading || rewardsToDisplay.length === 0);

  const getRewardKey = (reward: TPayLinkReward | null) =>
    reward?.uniqueReferenceNumber ||
    reward?.promotionUniqueReferenceNumber ||
    reward?.rewardName ||
    "";

  const selectedShippingKey = getRewardKey(selectedShippingReward);
  const selectedRegularKey = getRewardKey(selectedReward);
  const autoShippingReward = shippingRewards[0] || null;
  const autoShippingKey = getRewardKey(autoShippingReward);

  const [applyingKey, setApplyingKey] = useState<string | null>(null);

  useEffect(() => {
    if (applyingKey === null) return;
    const timer = setTimeout(() => setApplyingKey(null), 600);
    return () => clearTimeout(timer);
  }, [applyingKey]);

  useEffect(() => {
    if (hasShippingRewards) {
      const hasValidSelectedShipping = shippingRewards.some(
        (reward: TPayLinkReward | null) =>
          getRewardKey(reward) === selectedShippingKey,
      );

      if (!hasValidSelectedShipping && autoShippingReward) {
        setFreeShippingReward(autoShippingReward);
      }

      if (selectedReward?.rewardEffect === RewardRuleEffects.FreeShipping) {
        setReward(null);
      }

      return;
    }

    if (selectedShippingReward) {
      setFreeShippingReward(null);
    }
  }, [
    autoShippingReward,
    hasShippingRewards,
    selectedReward,
    selectedShippingKey,
    selectedShippingReward,
    setFreeShippingReward,
    setReward,
    shippingRewards,
  ]);

  const handleSelectReward = (r: TPayLinkReward) => {
    const isShippingReward = r?.rewardEffect === RewardRuleEffects.FreeShipping;
    const rewardKey = getRewardKey(r);

    if (hasShippingRewards && isShippingReward) {
      // Shipping reward is auto-selected/locked when any shipping reward exists.
      return;
    }

    setApplyingKey(rewardKey);

    if (!hasShippingRewards) {
      const isSameRegularSelection = selectedRegularKey === rewardKey;

      if (isSameRegularSelection) {
        setReward(null);
        setFreeShippingReward(null);
        return;
      }

      setReward(r);
      setFreeShippingReward(null);
      return;
    }

    const isSameRegularSelection = selectedRegularKey === rewardKey;

    if (isSameRegularSelection) {
      setReward(null);
      return;
    }

    setReward(r);
  };

  const toSpacedText = (value: string) =>
    value
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .trim();

  const getRewardDescriptions = (reward: TPayLinkReward | null): string[] => {
    if (!reward) return [];

    const fromRules = (Array.isArray(reward.rewardRules) ? reward.rewardRules : [])
      .map((rule: any) => {
        const name = rule?.attributeName ? toSpacedText(String(rule.attributeName)) : "";
        const criteria = rule?.valueCriteria
          ? toSpacedText(String(rule.valueCriteria)).toLowerCase()
          : "";
        const value = rule?.value ?? "";

        if (!name && !criteria && !value) return "";
        return `${name} ${criteria} ${value}`.replace(/\s+/g, " ").trim();
      })
      .filter(Boolean);

    if (fromRules.length > 0) return fromRules;

    if (reward.rewardReason) return [reward.rewardReason];

    return ["Reward rules are available for this offer."];
  };

  const getShippingRewardTitle = (reward: TPayLinkReward | null) => {
    if (!reward) return "Shipping reward";
    const cap = Number(reward.rewardEffectValueLimit ?? 0) / 100;
    if (cap <= 0) return reward.rewardName || "Free shipping reward";

    return `Free shipping up to ₦${new Intl.NumberFormat("en-NG").format(cap)}`;
  };

  if (shouldShowEnrolmentLoader) {
    return <Loader isFullPage={true} />;
  }

  if (isLoading) {
    return (
      <div className="w-full h-20 flex items-center justify-center">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (rewardsToDisplay.length === 0) {
    return (
      <p className="text-sm text-py_black-500">
        No available rewards at the moment.
      </p>
    );
  }

  return (
    <>
      <div className="d-flex gap-3 flex-column">
        {hasShippingRewards && autoShippingReward && (
          <RadioCardBox>
            <div className="selected card rounded-[0.75rem] !cursor-not-allowed opacity-90">
              <span className="radioOuter selected">
                <span className="radioInner" />
              </span>

              <div className="flex-1">
                <h3 className="title mb-2">
                  {getShippingRewardTitle(autoShippingReward)}
                </h3>

                {/* {getRewardDescriptions(autoShippingReward).map((line, idx) => (
                  <p key={idx} className="balance mb-1">
                    {line}
                  </p>
                ))} */}

                <span className="sectionNote">
                  Shipping rewards are automatically selected and applied to your order
                </span>
              </div>
            </div>
          </RadioCardBox>
        )}

        {hasShippingRewards && regularRewards.length > 0 && (
          <p className="helperText mb-0">
            Choose one more reward in addition to your shipping reward
          </p>
        )}

        {(hasShippingRewards ? regularRewards : rewardsToDisplay).map(
          (reward: TPayLinkReward | null, index: number) => {
            const rewardKey = getRewardKey(reward);
            const isSelected = selectedRegularKey === rewardKey;
            const isApplying = applyingKey === rewardKey;

            return (
              <RadioCardBox key={`${index}-${rewardKey}`}>
                <div
                  className={`${isSelected ? "selected" : ""} card rounded-[0.75rem]`}
                  onClick={() => handleSelectReward(reward as TPayLinkReward)}>
                  <span className={`radioOuter ${isSelected ? "selected" : ""}`}>
                    {isSelected && <span className="radioInner" />}
                  </span>

                  <div className="flex-1">
                    <h3 className="title mb-2">
                      {reward?.rewardName || "Reward"}
                    </h3>

                    {/* {getRewardDescriptions(reward).map((line, idx) => (
                      <p key={idx} className="balance mb-1">
                        {line}
                      </p>
                    ))} */}
                  </div>

                  {isApplying && (
                    <div className="ml-auto flex items-center gap-1 text-xs text-py_black_600">
                      <CircularProgress size={14} />
                    </div>
                  )}
                </div>
              </RadioCardBox>
            );
          },
        )}

        <p className="helperText mb-0">
          Your order total will be adjusted based on any of the rewards you select and apply
        </p>
      </div>
    </>
  );
};
